import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { BookOpen, HeartHandshake, ShieldCheck, Sparkles, Sprout, TrendingUp, Trophy } from "lucide-react";
import { OUTCOMES, REAL_CHANGE, STATS } from "./data";

const ease = [0.16, 1, 0.3, 1];
const OUTCOME_ICONS = [TrendingUp, HeartHandshake, BookOpen, ShieldCheck, Sprout, Trophy];

const Counter = ({ end }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1500;
    const t0 = performance.now();
    const tick = (now) => {
      const prog = Math.min((now - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - prog, 3);
      setVal(Math.round(eased * end));
      if (prog < 1) requestAnimationFrame(tick);
      else setVal(end);
    };
    requestAnimationFrame(tick);
  }, [inView, end]);
  return <span ref={ref}>{val}</span>;
};

export const Outcomes = () => (
  <section id="outcomes" className="relative py-24 md:py-36 bg-sand/50" data-testid="outcomes">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="mb-14 max-w-2xl">
        <p className="text-sm md:text-base tracking-[0.22em] uppercase font-semibold text-terra mb-5">The Impact for your school</p>
        <h2 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
          Outcomes that <span className="italic text-terra">last</span>.
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="border-t border-ink/15">
            {OUTCOMES.map((o, i) => {
              const Icon = OUTCOME_ICONS[i % OUTCOME_ICONS.length];
              return (
                <motion.div
                  key={o}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease, delay: i * 0.05 }}
                  data-testid={`outcome-${i}`}
                  className="flex items-center gap-4 py-5 border-b border-ink/15 group"
                >
                  <span className="w-10 h-10 shrink-0 bg-pine flex items-center justify-center group-hover:bg-terra transition-colors duration-300">
                    <Icon className="w-5 h-5 text-terra group-hover:text-white transition-colors" strokeWidth={2} />
                  </span>
                  <span className="md:text-xl font-medium">{o}</span>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease }}
            className="grid grid-cols-2 gap-px bg-ink/15 border border-ink/15"
            data-testid="impact-stats"
          >
            {STATS.map((s, i) => (
              <div key={s.label} className="bg-bone p-6 md:p-8" data-testid={`stat-${i}`}>
                <span className="font-serif text-5xl md:text-6xl leading-none">
                  <Counter end={s.end} /><span className="text-terra">{s.suffix}</span>
                </span>
                <p className="mt-3 text-ink/60 text-sm">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease }}
        className="mt-10 lg:mt-14 bg-pine text-bone p-8 md:p-12 relative overflow-hidden"
        data-testid="real-change"
      >
        <div className="noise-overlay" />
        <div className="relative">
          <span className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-terra">
            <Sparkles className="w-4 h-4" /> {REAL_CHANGE.label}
          </span>
          <p className="mt-5 font-serif text-2xl md:text-3xl leading-snug">{REAL_CHANGE.subject}</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-terra text-sm tracking-[0.15em] uppercase mb-3">Before JD Sir</p>
              <ul className="space-y-2 text-bone/75 text-sm">
                {REAL_CHANGE.before.map((b) => <li key={b}>— {b}</li>)}
              </ul>
            </div>
            <div>
              <p className="text-terra text-sm tracking-[0.15em] uppercase mb-3">After JD Sir</p>
              <ul className="space-y-2 text-bone/90 text-sm">
                {REAL_CHANGE.after.map((a) => <li key={a}>— {a}</li>)}
              </ul>
            </div>
          </div>
          <p className="mt-8 text-xs text-bone/45 border-t border-bone/15 pt-4">{REAL_CHANGE.note}</p>
        </div>
      </motion.div>
    </div>
  </section>
);
